import { useMemo, useState } from 'react';
import { ATOM_LIMITS, atomInfo } from '@chemai/engine';
import { elementByNumber, elementName } from '@chemai/data';
import { type MessageKey, useI18n } from '../../i18n';
import { SaveButton, SimBadge, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';
import { supConfig } from '../PeriodicTablePage';

type Particle = 'protons' | 'neutrons' | 'electrons';

/** Deterministic packing of nucleons in a small disc (sunflower spiral). */
function nucleonPositions(count: number, r: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < count; i++) {
    const rr = r * Math.sqrt((i + 0.5) / Math.max(count, 1));
    const a = i * 2.39996;
    out.push([rr * Math.cos(a), rr * Math.sin(a)]);
  }
  return out;
}

export function AtomSim() {
  const { t, lang } = useI18n();
  const [p, setP] = useState(6);
  const [n, setN] = useState(6);
  const [e, setE] = useState(6);
  const info = useMemo(() => atomInfo(p, n, e), [p, n, e]);
  const el = elementName(elementByNumber(p)!, lang);

  const limits: Record<Particle, [number, number]> = {
    protons: [1, ATOM_LIMITS.maxProtons],
    neutrons: [0, ATOM_LIMITS.maxNeutrons],
    electrons: [0, p + ATOM_LIMITS.maxExtraElectrons],
  };
  const value: Record<Particle, number> = { protons: p, neutrons: n, electrons: e };
  const set: Record<Particle, (v: number) => void> = {
    protons: (v) => { setP(v); setE((x) => Math.min(x, v + ATOM_LIMITS.maxExtraElectrons)); },
    neutrons: setN,
    electrons: setE,
  };

  const nucleusR = 10 + Math.sqrt(p + n) * 4.2;
  const nucleons = nucleonPositions(p + n, nucleusR - 5);
  // interleave protons and neutrons so the nucleus looks mixed
  const kinds = Array.from({ length: p + n }, (_, i) => (Math.floor((i * p) / (p + n)) !== Math.floor(((i + 1) * p) / (p + n)) ? 'p' : 'n'));
  const shellR = [nucleusR + 22, nucleusR + 48, nucleusR + 74, nucleusR + 98];

  const stabilityKey: MessageKey = info.stability === 'stable' ? 'atom.stable' : info.stability === 'primordial-radioactive' ? 'atom.primordial' : 'atom.unstable';
  return (
    <div className="sim">
      <SimBadge />
      <div className="sim-grid">
        <section className="panel">
          <h3>{t('atom.particles')}</h3>
          {(['protons', 'neutrons', 'electrons'] as Particle[]).map((k) => {
            const [min, max] = limits[k];
            const label = t(`atom.${k}` as MessageKey);
            return (
              <div className="stepper" key={k}>
                <span className={`dot dot-${k}`} aria-hidden="true" />
                <span className="stepper-label" id={`lbl-${k}`}>{label}</span>
                <button type="button" className="icon-btn" aria-label={t('atom.remove', { p: label.toLowerCase() })} disabled={value[k] <= min} onClick={() => set[k](value[k] - 1)}>−</button>
                <output className="stepper-value" aria-labelledby={`lbl-${k}`} aria-live="polite">{value[k]}</output>
                <button type="button" className="icon-btn" aria-label={t('atom.add', { p: label.toLowerCase() })} disabled={value[k] >= max} onClick={() => set[k](value[k] + 1)}>+</button>
              </div>
            );
          })}
          <p className="hint">{t('atom.explain')}</p>
          <p className="muted small">{t('atom.limit')}</p>
        </section>

        <section className="panel apparatus-panel">
          <h3>{t('atom.model')}</h3>
          <svg viewBox="-160 -160 320 320" className="apparatus atom-svg" role="img"
            aria-label={`${el}: ${p} p, ${n} n, ${e} e; ${info.shells.join('-')}`}>
            {shellR.slice(0, Math.max(info.shells.length, 1)).map((r, i) => (
              <circle key={i} r={r} className="shell" />
            ))}
            {nucleons.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r={5.2} className={kinds[i] === 'p' ? 'proton' : 'neutron'} />
            ))}
            {info.shells.map((count, si) =>
              Array.from({ length: count }, (_, k) => {
                const a = (k / count) * Math.PI * 2 - Math.PI / 2 + si * 0.4;
                return <circle key={`${si}-${k}`} cx={shellR[si] * Math.cos(a)} cy={shellR[si] * Math.sin(a)} r={4.5} className="electron" />;
              }),
            )}
          </svg>
          <p className="observation">{info.shells.length ? info.shells.join(' – ') : '—'}</p>
        </section>

        <section className="panel readouts" aria-live="polite">
          <h3>{t('lab.observations')}</h3>
          <div className="nuclide" aria-label={`${info.symbol}, A = ${info.massNumber}, Z = ${p}, ${info.chargeLabel || '0'}`}>
            <span className="nuclide-a">{info.massNumber}</span>
            <span className="nuclide-z">{p}</span>
            <span className="nuclide-sym">{info.symbol}</span>
            <span className="nuclide-q">{info.chargeLabel}</span>
          </div>
          <div className="stats">
            <Stat label={t('atom.element')} value={el} emphasis />
            <Stat label={t('atom.massNumber')} value={info.massNumber} />
            <Stat label={t('atom.charge')} value={info.charge > 0 ? `+${info.charge}` : info.charge < 0 ? `−${-info.charge}` : '0'} />
            <Stat label={t('atom.kind')} value={t(`atom.kind.${info.kind}` as MessageKey)} />
            <Stat label={t('atom.stability')} value={t(stabilityKey)} />
          </div>
          <h4>{t('atom.config')}</h4>
          <p className="config mono">{supConfig(info.configuration)}</p>
          <SaveButton onSave={() => progress.recordExperiment('atom', { p, n, e, sym: info.symbol, a: info.massNumber, q: info.chargeLabel })} />
        </section>
      </div>
    </div>
  );
}
